import { useEffect, useState } from "react";
import { StarDecorations } from "components/ui/StarDecorations";
import { buscarEntregasPorGestor, atualizarEntrega } from "services/entrega.service";
import { useAuth } from "context/AuthContext";
import { FiSearch } from "react-icons/fi";
import { FaCheck, FaTimes } from "react-icons/fa";
import { CardIniciativa } from "components/ui/CardIniciativa";
import { ModalEditarIniciativa } from "components/ui/ModalEditarIniciativa";

interface ApprovalLogEntry {
  action: "aprovada" | "reprovada" | "aguardando" | "editada";
  user_id: string;
  user_name: string;
  user_matricula: string;
  timestamp: string;
}

interface Entrega {
  _id: string;
  title: string;
  description: string;
  indicator: {
    nome: string;
    cod: string;
  };
  owner: string;
  owner_name: string;
  owner_name_guerra: string;
  owner_funcao: string;
  owner_nome_dependencia: string;
  owner_avatar: any;
  start: number;
  end: number;
  status: string;
  team: Array<{ _id: string; name?: string; code?: string }>;
  evaluator_detail: any[];
  non_evaluator_detail: any[];
  prefixo: number;
  diretoria: string;
  result: string;
  original_manager: string;
  original_manager_detail: any;
  mad: boolean;
  approval_log?: ApprovalLogEntry[];
}

export function ValidacaoIniciativas() {
  const [entregas, setEntregas] = useState<Entrega[]>([]);
  const [entregasFiltradas, setEntregasFiltradas] = useState<Entrega[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");
  const [termoBusca, setTermoBusca] = useState<string>("");
  const [entregaEditando, setEntregaEditando] = useState<Entrega | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    async function carregarEntregas() {
      try {
        setLoading(true);
        const data = await buscarEntregasPorGestor({
          player: user?._id || "",
          start: "2026-01-01T00:00:00-03:00",
        });
        setEntregas(data || []);
        setEntregasFiltradas(data || []);
      } catch (error) {
        console.error("Erro ao carregar entregas:", error);
        setEntregas([]);
        setEntregasFiltradas([]);
      } finally {
        setLoading(false);
      }
    }

    carregarEntregas();
  }, [user]);

  useEffect(() => {
    let resultados = [...entregas];

    // Filtrar por status
    if (filtroStatus === "aprovadas") {
      resultados = resultados.filter((e) => e.status === "aprovada");
    } else if (filtroStatus === "reprovadas") {
      resultados = resultados.filter((e) => e.status === "reprovada");
    } else if (filtroStatus === "aguardando") {
      resultados = resultados.filter((e) => e.status === "aguardando");
    }

    // Filtrar por termo de busca
    if (termoBusca.trim()) {
      const termo = termoBusca.toLowerCase();
      resultados = resultados.filter(
        (e) =>
          e.title?.toLowerCase().includes(termo) ||
          e.indicator?.nome?.toLowerCase().includes(termo) ||
          e.owner_name?.toLowerCase().includes(termo)
      );
    }

    setEntregasFiltradas(resultados);
  }, [filtroStatus, termoBusca, entregas]);

  const handleAprovar = async (entregaId: string) => {
    try {
      const entrega = entregas.find((e) => e._id === entregaId);
      if (!entrega) return;

      const logEntry: ApprovalLogEntry = {
        action: "aprovada",
        user_id: user?._id || "",
        user_name: user?.detail?.nome_guerra || user?.detail?.nome || "",
        user_matricula: user?._id || "",
        timestamp: new Date().toISOString(),
      };
      const approval_log = [...(entrega.approval_log || []), logEntry];

      await atualizarEntrega({
        ...entrega,
        status: "aprovada",
        approval_log,
        updated: new Date().toISOString(),
      });

      // Atualizar o estado local
      setEntregas((prev) =>
        prev.map((e) =>
          e._id === entregaId ? { ...e, status: "aprovada", approval_log } : e
        )
      );
    } catch (error) {
      console.error("Erro ao aprovar iniciativa:", error);
    }
  };

  const handleReprovar = async (entregaId: string) => {
    try {
      const entrega = entregas.find((e) => e._id === entregaId);
      if (!entrega) return;

      const logEntry: ApprovalLogEntry = {
        action: "reprovada",
        user_id: user?._id || "",
        user_name: user?.detail?.nome_guerra || user?.detail?.nome || "",
        user_matricula: user?._id || "",
        timestamp: new Date().toISOString(),
      };
      const approval_log = [...(entrega.approval_log || []), logEntry];

      await atualizarEntrega({
        ...entrega,
        status: "reprovada",
        approval_log,
        updated: new Date().toISOString(),
      });

      // Atualizar o estado local
      setEntregas((prev) =>
        prev.map((e) =>
          e._id === entregaId ? { ...e, status: "reprovada", approval_log } : e
        )
      );
    } catch (error) {
      console.error("Erro ao reprovar iniciativa:", error);
    }
  };

  const handleDesfazer = async (entregaId: string) => {
    try {
      const entrega = entregas.find((e) => e._id === entregaId);
      if (!entrega) return;

      const logEntry: ApprovalLogEntry = {
        action: "aguardando",
        user_id: user?._id || "",
        user_name: user?.detail?.nome_guerra || user?.detail?.nome || "",
        user_matricula: user?._id || "",
        timestamp: new Date().toISOString(),
      };
      const approval_log = [...(entrega.approval_log || []), logEntry];

      await atualizarEntrega({
        ...entrega,
        status: "aguardando",
        approval_log,
        updated: new Date().toISOString(),
      });

      // Atualizar o estado local
      setEntregas((prev) =>
        prev.map((e) =>
          e._id === entregaId ? { ...e, status: "aguardando", approval_log } : e
        )
      );
    } catch (error) {
      console.error("Erro ao desfazer ação:", error);
    }
  };

  const handleEditar = (entregaId: string) => {
    const entrega = entregas.find((e) => e._id === entregaId);
    if (entrega) setEntregaEditando(entrega);
  };

  const handleSalvarEdicao = (entregaAtualizada: Entrega) => {
    setEntregas((prev) =>
      prev.map((e) => (e._id === entregaAtualizada._id ? entregaAtualizada : e))
    );
    setEntregaEditando(null);
  };

  const contadorStatus = {
    aprovadas: entregas.filter((e) => e.status === "aprovada").length,
    reprovadas: entregas.filter((e) => e.status === "reprovada").length,
    aguardando: entregas.filter((e) => e.status === "aguardando").length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-bb via-blue-bb-1 to-blue-bb-5 overflow-x-hidden relative">
      {/* Star Decorations - Hidden on Mobile */}
      <div aria-hidden="true" className="hidden md:block pointer-events-none">
        <StarDecorations count={18} density="medium" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12 max-w-6xl relative z-10">
        <header className="mb-6 sm:mb-7 lg:mb-8">
          <h1 className="text-green-bb-2 text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 leading-tight">
            Valide as iniciativas
          </h1>
          <p className="text-white text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed">
            Confira as iniciativas inscritas no Prêmio ELBB.{" "}
            <span className="text-green-bb-2 font-semibold">Valide</span>
            {" "}as que mais inspiram e transformam.
          </p>
        </header>

        <div className="mb-5 sm:mb-6">
          <p className="text-white text-base sm:text-lg font-semibold mb-3 sm:mb-4">
            Total de iniciativas: {entregas.length}
          </p>

          {/* Filtros */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-5 sm:mb-6">
            <div className="relative">
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              >
                <option value="todos">Filtrar por status</option>
                <option value="aprovadas">Validadas</option>
                <option value="reprovadas">Não validadas</option>
                <option value="aguardando">Aguardando</option>
              </select>
            </div>

            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="O que você procura?"
                value={termoBusca}
                onChange={(e) => setTermoBusca(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              />
            </div>
          </div>
        </div>

        {/* Layout com Contadores e Listagem */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
          {/* Contadores - Coluna Esquerda */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-lg lg:sticky lg:top-6">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <FaCheck className="text-green-600 w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <label className="text-gray-700 font-medium text-xs sm:text-sm">
                  Validadas ({contadorStatus.aprovadas})
                </label>
              </div>
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <FaTimes className="text-red-600 w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <label className="text-gray-700 font-medium text-xs sm:text-sm">
                  Não validadas ({contadorStatus.reprovadas}/{entregas.length})
                </label>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <label className="text-gray-700 font-medium text-xs sm:text-sm">
                  Aguardando ({contadorStatus.aguardando})
                </label>
              </div>
            </div>
          </div>

          {/* Listagem - Coluna Direita */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl sm:shadow-2xl">
              {/* Lista de iniciativas */}
              {loading ? (
                <p className="text-gray-600 text-center text-base sm:text-lg py-8">
                  Carregando iniciativas...
                </p>
              ) : entregasFiltradas.length === 0 ? (
                <p className="text-gray-600 text-center text-base sm:text-lg py-8">
                  Nenhuma iniciativa encontrada.
                </p>
              ) : (
                <div className="space-y-4 sm:space-y-5 lg:space-y-6">
                  {entregasFiltradas.map((entrega) => (
                    <CardIniciativa
                      key={entrega._id}
                      entrega={entrega}
                      onAprovar={handleAprovar}
                      onReprovar={handleReprovar}
                      onDesfazer={handleDesfazer}
                      onEditar={handleEditar}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {entregaEditando && (
        <ModalEditarIniciativa
          entrega={entregaEditando}
          onSave={handleSalvarEdicao}
          onClose={() => setEntregaEditando(null)}
          currentUser={user}
        />
      )}
    </div>
  );
}
